
import path from 'path';
import fs from 'fs';
import { prop, tap } from 'ramda';
import { log, error } from '@bb-cli/base';
import { loadItem, findItemFiles } from '@bb-cli/lib-find-backbase-items';
import { unzip } from './zip';

const MANIFEST_FILE = 'manifest.json';
const ITEM_TYPE_CATALOG = 'catalog';
const LOG_PREFIX = 'EXTRACT';

const isCatalogItem = item => item.itemType === ITEM_TYPE_CATALOG;

const logExtractedItems = (extractedItems) => {
  log.verbose(LOG_PREFIX, `Extracted ${extractedItems.length} items`);
  log.silly(LOG_PREFIX, 'Extracted CXP items', extractedItems);
};

/**
 * @inner
 * @param {string} extractTo
 * @return {Array.<CXPItem>}
 */
const extractInnerPackage = (extractTo) => (file) => {
  const extractInnerTo = path.join(extractTo, `${file}.extract`);
  return unzip(path.join(extractTo, file), extractInnerTo)
    .then(() => loadItem(extractInnerTo));
};

/**
 * @inner
 * @param {UnzipItem} item
 * @return {Boolean}
 */
const isUnzippedFile = item => !!item.deflated;

/**
 * @inner
 * @param {Array.<UnzipItem>} unzipped
 * @return {Array.<string>} zip files
 */
const readInnerZips = (unzipped) =>
  unzipped.filter(isUnzippedFile)
    .map(prop('deflated'))
    .filter(file => file.match(/\.zip$/));

/**
 * @inner
 * @param {string} manifestFile Absolute path to manifest file
 * @return {Array.<string>} zip files
 */
const readManifestCatalogItems = (manifestFile) => {
  try {
    const manifest = JSON.parse(fs.readFileSync(manifestFile));
    if (!manifest.provisioningItems || !Array.isArray(manifest.provisioningItems)) {
      throw error({
        type: 'E_PARSE_MANIFEST',
        message: 'Error parsing manifest. Missing provisioningItems array',
      });
    }
    return manifest.provisioningItems.filter(isCatalogItem).map(prop('location'));
  } catch (err) {
    log.error(LOG_PREFIX, 'Error parsing the manifest', err);
    throw err;
  }
};

/**
 * @inner
 * @param {String} extractTo Abolute path to dir that was unzipped into
 * @param {Array.<UnzipItem>} unzipped
 * @return {Array.<string>} zip files
 */
const filterCatalogZips = (extractTo) => (unzipped) => {
  const hasManifest = unzipped.find(file => file.deflated === MANIFEST_FILE);
  if (!hasManifest) {
    log.warn(LOG_PREFIX, 'No manifest found, continuing by extracting all inner zips');
    return readInnerZips(unzipped);
  }
  return readManifestCatalogItems(path.join(extractTo, MANIFEST_FILE));
};

/**
 * Extract a provision package (zip of zips).
 *
 * @memberof module:@bb-cli/bb-package
 * @param {string} packageFile Path to provision package (zip of zips)
 * @param {string} extractTo The path to extract to
 * @return {Promise.<CxpItems[]>} The CXP items in the package, with the 'files' property set
 * @example
 *
 * ```
 * extractPackage('package.zip', 'dist')
 *   .then(items => console.log(`Extracted ${items.length}`));
 * ```
 */
const extractPackage = (packageFile, extractTo) =>
  unzip(packageFile, extractTo)
    .then(filterCatalogZips(extractTo))
    .then(files => Promise.all(files.map(extractInnerPackage(extractTo))))
    .then(findItemFiles)
    .then(tap(logExtractedItems));

export default extractPackage;

/**
 * @inner
 * @typedef UnzipItem
 * @type {Object}
 * @property {?String} deflated Optional name of the unzipped file (not set for directories)
 */
